import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    Button,
    useSidebar,
} from '@duck/ui'
import React, { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Home, Trophy, Activity, Info, HelpCircle, User, ClipboardList, Megaphone, UserPlus, BookOpen, MessageCircle, Brain, ClipboardCheck, MessageSquarePlus } from 'lucide-react'
import { useTar } from '@/modules/i18n/useTar'
import { useOngoingChallenges } from '@/hooks/useOngoingChallenges'
import { useReleaseNotes } from '@/hooks/useReleaseNotes'
import { useShowJoinGamma } from '@/hooks/useShowJoinGamma'
import { SideBarAdmin } from './SideBarAdmin'
import { SideBarPartner } from './SideBarPartner'
import { useAuth } from '@/hooks/useAuth'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { toast } from 'sonner'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { useUserVoice } from '@/hooks/useUserVoice'

type SideBarMenuProps = {
    open: boolean
    handleMobileNav: () => void
}

export const SideBarMenu = ({ open, handleMobileNav }: SideBarMenuProps) => {
    const { t } = useTar()
    const { isMobile } = useSidebar()
    // The collapse/expand toggle (`open`) is a desktop-only affordance. On mobile
    // the sidebar is a full sheet, so items should always render expanded and never
    // hide their sub-menus in response to the desktop collapsed state.
    const expanded = open || isMobile
    const { challenges: ongoingChallenges, assignments: activeAssignments } = useOngoingChallenges()
    const { isAdmin, isPartner, isPerson, isAuthenticated } = useAuth()
    const { showJoinGammaCta } = useShowJoinGamma()
    const { unreadCount, fetchNotes, isBlocked: hasAssignmentOnlyBadge } = useReleaseNotes(true, false)
    const navigate = useNavigate()
    const [isJoiningGamma, setIsJoiningGamma] = useState(false)
    const { openVoice } = useUserVoice()

    useDebouncedEffect(() => {
        if (isAuthenticated) {
            fetchNotes(false)
        }
    }, [isAuthenticated, fetchNotes], 200)

    const handleJoinGamma = async () => {
        if (isJoiningGamma) return
        setIsJoiningGamma(true)
        try {
            await apis.userService.joinGammaPost()
            navigate({ to: '/challenges/available' })
            handleMobileNav()
        } catch (error) {
            console.error('Failed to join Gamma:', error)
            toast.error('Failed to join. Please try again.')
        } finally {
            setIsJoiningGamma(false)
        }
    }

    const ongoingAssignments = activeAssignments.filter(assignment => assignment.slug)
    const assignmentSlugs = new Set(ongoingAssignments.map(a => a.slug))
    const ongoingChallengesWithoutAssignments = ongoingChallenges.filter(
        challenge => !assignmentSlugs.has(challenge.slug)
    )

    return (
      <>
        {isPartner && (
          <SideBarPartner open={open} handleMobileNav={handleMobileNav} />
        )}

        {isAdmin && (
          <SideBarAdmin open={open} handleMobileNav={handleMobileNav} />
        )}
        {/* Main Navigation Section */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('common.home')}>
                  <LocalizedLink to="/" onClick={handleMobileNav}>
                    <Home />
                    <span>{t('common.home')}</span>
                  </LocalizedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {isAuthenticated && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t('common.userArea')}>
                    <Link to="/user-area" onClick={handleMobileNav}>
                      <User />
                      <span>{t('common.userArea')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isAuthenticated && !hasAssignmentOnlyBadge && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip={t('releaseNotes.whatsNew')}
                  >
                    <Link to="/updates" onClick={handleMobileNav}>
                      <Megaphone />
                      <span>{t('releaseNotes.whatsNew')}</span>
                      {unreadCount > 0 && (
                        <SidebarMenuBadge className="border-primary/80 border rounded-xl" data-testid="release-notes-unread-badge">
                          {unreadCount}
                        </SidebarMenuBadge>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isAuthenticated && !hasAssignmentOnlyBadge && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t('refer.nav')}>
                    <Link to="/invite" onClick={handleMobileNav}>
                      <UserPlus />
                      <span>{t('refer.nav')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isPerson && activeAssignments.length > 0 && (
                <SidebarMenuItem>
                  <div>
                    <SidebarMenuButton asChild tooltip={t('partner.assignments')}>
                      <Link to="/assignments" onClick={handleMobileNav}>
                        <ClipboardList />
                        <span>{t('partner.assignments')}</span>
                        <SidebarMenuBadge className="border-primary/80 border rounded-xl">
                          {activeAssignments.length}
                        </SidebarMenuBadge>
                      </Link>
                    </SidebarMenuButton>
                  </div>
                  {ongoingAssignments.length > 0 && (
                    <SidebarMenuSub>
                      {ongoingAssignments.map(
                        (assignment) =>
                          assignment.slug && (
                            <SidebarMenuSubButton
                              key={assignment.assignmentId}
                              asChild
                            >
                              <Link
                                to="/challenge/$slug"
                                params={{ slug: assignment.slug }}
                                onClick={handleMobileNav}
                              >
                                <span>{assignment.name}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          )
                      )}
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                {!expanded ? (
                  <SidebarMenuButton asChild tooltip={t('navigation.challenges')}>
                    <Link to="/challenges/available" onClick={handleMobileNav}>
                      <Trophy />
                      <span>{t('navigation.challenges')}</span>
                    </Link>
                  </SidebarMenuButton>
                ) : (
                  <>
                    <SidebarMenuButton asChild>
                      <div className="pointer-events-none">
                        <Trophy />
                        <span>{t('navigation.challenges')}</span>
                      </div>
                    </SidebarMenuButton>
                    <SidebarMenuSub>
                      <SidebarMenuSubButton asChild>
                        <Link
                          to="/challenges/available"
                          onClick={handleMobileNav}
                        >
                          <span>{t('challenges.available_challenges_')}</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton asChild>
                        <Link
                          to="/challenges/completed"
                          onClick={handleMobileNav}
                        >
                          <span>{t('challenges.completed_challenges_')}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSub>
                  </>
                )}
              </SidebarMenuItem>
              {ongoingChallengesWithoutAssignments.length > 0 && (
                <SidebarMenuItem>
                  {!expanded ? (
                    <SidebarMenuButton asChild tooltip={t('navigation.ongoingChallenges')}>
                      <Link
                        to="/challenge/$slug"
                        params={{ slug: ongoingChallengesWithoutAssignments[0].slug }}
                        onClick={handleMobileNav}
                      >
                        <Activity />
                        <span>{t('navigation.ongoingChallenges')}</span>
                        <SidebarMenuBadge>
                          {ongoingChallengesWithoutAssignments.length}
                        </SidebarMenuBadge>
                      </Link>
                    </SidebarMenuButton>
                  ) : (
                    <>
                      <SidebarMenuButton asChild>
                        <div className="pointer-events-none">
                          <Activity />
                          <span>{t('navigation.ongoingChallenges')}</span>
                          <SidebarMenuBadge>
                            {ongoingChallengesWithoutAssignments.length}
                          </SidebarMenuBadge>
                        </div>
                      </SidebarMenuButton>
                      <SidebarMenuSub>
                        {ongoingChallengesWithoutAssignments.slice(0, 6).map((challenge) => (
                          <SidebarMenuSubButton
                            key={challenge.challengeId || challenge.slug}
                            asChild
                          >
                            <Link
                              to="/challenge/$slug"
                              params={{ slug: challenge.slug }}
                              onClick={handleMobileNav}
                            >
                              <span>{challenge.name}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        ))}
                      </SidebarMenuSub>
                    </>
                  )}
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('navigation.aboutUs')}>
                  <LocalizedLink to="/about" onClick={handleMobileNav}>
                    <Info />
                    <span>{t('navigation.aboutUs')}</span>
                  </LocalizedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('navigation.faq')}>
                  <LocalizedLink to="/faq" onClick={handleMobileNav}>
                    <HelpCircle />
                    <span>{t('navigation.faq')}</span>
                  </LocalizedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {isAuthenticated && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t('userVoice.menuItem')}
                    onClick={() => {
                      openVoice({ source: 'sidebar' })
                      handleMobileNav?.()
                    }}
                  >
                    <MessageSquarePlus />
                    <span>{t('userVoice.menuItem')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
            {showJoinGammaCta && (
              <div
                className="px-2 mt-4"
                style={{
                  border: '3px solid transparent',
                  backgroundImage:
                    'linear-gradient(hsl(var(--color-background)), hsl(var(--color-background))), linear-gradient(to right, #6ee7b7, #047857)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                  borderRadius: '15px',
                }}
              >
                <div className="flex items-center justify-center gap-2 py-2">
                  <Button
                    variant="link"
                    className="h-auto px-0 py-0"
                    onClick={handleJoinGamma}
                    disabled={isJoiningGamma}
                  >
                    {t('common.joinOurBeta')}
                  </Button>
                </div>
              </div>
            )}
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('navigation.guides')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('navigation.guideAnalyticalTasks')}>
                  <LocalizedLink to="/analytical-workflow" onClick={handleMobileNav}>
                    <ClipboardCheck />
                    <span>{t('navigation.guideAnalyticalTasks')}</span>
                  </LocalizedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('navigation.guideFeedback')}>
                  <LocalizedLink to="/xp-lab-feedback" onClick={handleMobileNav}>
                    <MessageCircle />
                    <span>{t('navigation.guideFeedback')}</span>
                  </LocalizedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('navigation.guideAiJudgment')}>
                  <LocalizedLink to="/using-ai-without-outsourcing-judgment" onClick={handleMobileNav}>
                    <Brain />
                    <span>{t('navigation.guideAiJudgment')}</span>
                  </LocalizedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('navigation.guideTakeHomes')}>
                  <LocalizedLink to="/take-home-assignments" onClick={handleMobileNav}>
                    <BookOpen />
                    <span>{t('navigation.guideTakeHomes')}</span>
                  </LocalizedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </>
    )
}
